import React, { useEffect } from "react"
import { Form, Input, DatePicker, InputNumber, Button, message, Skeleton, Card, Checkbox, Typography, Divider } from "antd"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate, useParams } from "react-router-dom"
import { Link } from "react-router-dom"
import { ContainerFull } from "../components"
import { ArrowLeftOutlined } from "@ant-design/icons"
import { getFlowCells, createFlowCell, updateFlowCell, FlowCell } from "../endpoints"
import dayjs from "dayjs"

const { Text, Title } = Typography

const { TextArea } = Input

export const FlowCellEdit = (): JSX.Element => {
    const { flowCellId } = useParams<{ flowCellId: string }>()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const [form] = Form.useForm()
    const isEditing = Boolean(flowCellId && flowCellId !== 'new')

    const { isPending, data } = useQuery({
        queryKey: ["flowcells"],
        queryFn: () => getFlowCells(),
        enabled: isEditing,
        refetchOnMount: "always",
    })

    const createMutation = useMutation({
        mutationFn: createFlowCell,
        onSuccess: (response) => {
            message.success("Flow cell created successfully")
            queryClient.invalidateQueries({ queryKey: ["flowcells"] })
            if (response.flowcell?.id) {
                navigate(`/admin/flowcell/${response.flowcell.id}`, { replace: true })
            }
        },
        onError: (error: any) => {
            message.error(error?.message || "Failed to create flow cell")
        },
    })

    const updateMutation = useMutation({
        mutationFn: updateFlowCell,
        onSuccess: () => {
            message.open({ key: "flowcell-save", type: "success", content: "Flow cell saved", duration: 1 })
            queryClient.invalidateQueries({ queryKey: ["flowcells"] })
        },
        onError: (error: any) => {
            message.error(error?.message || "Failed to update flow cell")
        },
    })

    useEffect(() => {
        if (isEditing && data?.flowcells) {
            const flowCell = data.flowcells.find((fc: FlowCell) => fc.id === parseInt(flowCellId!))
            if (flowCell) {
                form.setFieldsValue({
                    id: flowCell.id,
                    name: flowCell.name,
                    poresStartedWith: flowCell.poresStartedWith,
                    poresLeft: flowCell.poresLeft,
                    startToUse: flowCell.startToUse ? dayjs(flowCell.startToUse) : null,
                    diedAt: flowCell.diedAt ? dayjs(flowCell.diedAt) : null,
                    washed: flowCell.washed,
                    notes: flowCell.notes,
                })
            }
        }
    }, [isEditing, flowCellId, data, form])

    const handleAutoSave = () => {
        if (!isEditing && createMutation.isPending) return
        form.validateFields().then((values) => {
            const formData = {
                name: values.name,
                poresStartedWith: values.poresStartedWith,
                poresLeft: values.poresLeft || null,
                startToUse: values.startToUse ? values.startToUse.toISOString() : null,
                diedAt: values.diedAt ? values.diedAt.toISOString() : null,
                washed: values.washed ?? true,
                notes: values.notes || "",
            }

            if (isEditing) {
                updateMutation.mutate({
                    flowCellId: parseInt(flowCellId!),
                    ...formData,
                })
            } else {
                if (!formData.name) return
                createMutation.mutate(formData)
            }
        }).catch(() => {})
    }

    if (isPending && isEditing) return <Skeleton />

    const currentFlowCell = isEditing && data?.flowcells
        ? data.flowcells.find((fc: FlowCell) => fc.id === parseInt(flowCellId!))
        : null
    const runs = currentFlowCell?.runs || []

    return (
        <ContainerFull style={{ fontFamily: '"Rubik", sans-serif' }}>
            <Card
                title={
                    <div>
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate("/admin/flowcells")}
                            style={{ marginTop: '2em', marginBottom: '2em' }}
                        />
                        <div>
                            {isEditing ? `Edit Flow Cell #${flowCellId}` : "Create Flow Cell"}
                        </div>
                    </div>
                }
            >
                <Form form={form} layout="vertical" style={{ maxWidth: 600 }}>
                    {isEditing && (
                        <Form.Item name="id" label="Flow Cell ID">
                            <Input disabled />
                        </Form.Item>
                    )}
                    <Form.Item
                        name="name"
                        label="Name"
                        rules={[{ required: true, message: "Please enter flow cell name" }]}
                    >
                        <Input
                            placeholder="e.g., FC001"
                            onBlur={handleAutoSave}
                            onPressEnter={handleAutoSave}
                        />
                    </Form.Item>

                    <Form.Item
                        name="poresStartedWith"
                        label="Pores Started With"
                        initialValue={1511}
                    >
                        <InputNumber
                            style={{ width: "100%" }}
                            min={0}
                            placeholder="e.g., 1511"
                            onBlur={handleAutoSave}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleAutoSave()
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="poresLeft"
                        label="Pores Left"
                    >
                        <InputNumber
                            style={{ width: "100%" }}
                            min={0}
                            placeholder="e.g., 1200"
                            onBlur={handleAutoSave}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleAutoSave()
                            }}
                        />
                    </Form.Item>

                    <Form.Item name="startToUse" label="Start to Use" initialValue={isEditing ? undefined : dayjs()}>
                        <DatePicker style={{ width: "100%" }} onChange={handleAutoSave} />
                    </Form.Item>

                    <Form.Item name="diedAt" label="Died On">
                        <DatePicker style={{ width: "100%" }} onChange={handleAutoSave} />
                    </Form.Item>

                    <Form.Item name="washed" valuePropName="checked" initialValue={true}>
                        <Checkbox onChange={handleAutoSave}>Washed</Checkbox>
                    </Form.Item>

                    <Form.Item name="notes" label="Notes">
                        <TextArea
                            rows={4}
                            placeholder="Enter notes about this flow cell..."
                            onBlur={handleAutoSave}
                        />
                    </Form.Item>
                </Form>

                {isEditing && (
                    <>
                        <Divider />
                        <Title level={5} style={{ marginBottom: 12 }}>Usage History</Title>
                        {runs.length === 0 ? (
                            <Text type="secondary">No usage history</Text>
                        ) : (
                            <div>
                                {runs.map((run: any, index: number) => (
                                    <div key={run.id} style={{ marginBottom: index < runs.length - 1 ? 6 : 0, fontFamily: "monospace", fontSize: 13 }}>
                                        <Link to={`/admin/run/${run.id}`} style={{ fontWeight: 500 }}>
                                            #{run.id}
                                        </Link>
                                        {" | "}
                                        <span>{new Date(run.ts).toLocaleDateString()}</span>
                                        {" | "}
                                        <span>n: {run.nSamples || "N/A"}</span>
                                        {" | "}
                                        <span>Time: {run.duration ? `${run.duration}h` : "N/A"}</span>
                                        {" | "}
                                        <span>Reads: {run.nreads ? `${(run.nreads / 1000).toFixed(0)}k` : "N/A"}</span>
                                        {" | "}
                                        <span>Pores: {run.pores?.toLocaleString() || "N/A"}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </Card>
        </ContainerFull>
    )
}
